import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
// logic
import { useTranslation } from 'react-i18next';
// redux
import { constants as loadConstants } from 'utils/loads';
import { useDispatch, useSelector } from 'react-redux';
import loadSelectors from 'state/loads/selectors';
import loadsActions from 'state/loads/actionCreators';
import { selectors as orderSelectors } from 'state/order';
import { selectors as settingsSelectors } from 'state/settings';
// styles
import styled, { css } from 'styled-components';
import { gridSizes } from 'utils/style';
// components
import { Title } from 'refactored/ui/typography';
import { RemoveButton } from 'refactored/ui/form';
import LoadType from './inputs/loadTypeSwitch';
import PalletTypeDropdown from './inputs/palletTypeDropdown';
import NonPalletizedTypeDrodown from './inputs/nonPalletizedTypeDrodown';
import NumberOfItemsInput from './inputs/numberOfItemsInput';
import LoadSizeInput from './inputs/loadSizeInput';
import { GirdedQuestionIcon } from 'refactored/ui/icons';
import LoadModal from './LoadModal';

const StyledGirdedQuestionIcon = styled(GirdedQuestionIcon)`
  cursor: pointer;
`;
const TitleContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const LoadInputs = ({
  className,
  shipmentKey,
  loadKey,
  loadNumber,
  hasMultipleLoads,
}) => {
  const { t } = useTranslation('LoadInfo');
  const dispatch = useDispatch();
  const isEditing = useSelector(orderSelectors.getIsEditing);

  const deleteLoad = useCallback(() => {
    dispatch(loadsActions.deleteLoad({ shipmentKey, loadKey }));
  }, [dispatch, shipmentKey, loadKey]);

  const selectedLoadType = useSelector((state) =>
    loadSelectors.getLoadType(state, shipmentKey, loadKey)
  );

  const isPallet = selectedLoadType === loadConstants.LOAD_TYPES.PALLET;

  const maximumTruckWeight = useSelector(
    settingsSelectors.getCountrySettings
  )?.maximum_truck_weight;

  const [showModal, setShowModal] = useState(false);

  return (
    <div className={className}>
      <div className="grid">
        <div className="grid header">
          <TitleContainer className="title">
            <Title level="h3">
              {t('title')} {hasMultipleLoads && loadNumber}
            </Title>
            <StyledGirdedQuestionIcon onClick={() => setShowModal(true)} />
            <LoadModal t={t} showModal={showModal} setShowModal={setShowModal}/>
          </TitleContainer>
          {!isEditing && hasMultipleLoads && (
            <RemoveButton
              handleRemove={() => deleteLoad(shipmentKey, loadKey)}
            />
          )}
        </div>
        <div className="grid inputs">
          <LoadType shipmentKey={shipmentKey} loadKey={loadKey} />         
          {isPallet && (
            <PalletTypeDropdown shipmentKey={shipmentKey} loadKey={loadKey} />
          )}
          {!isPallet && (
            <NonPalletizedTypeDrodown
              shipmentKey={shipmentKey}
              loadKey={loadKey}
            />
          )}
          <NumberOfItemsInput shipmentKey={shipmentKey} loadKey={loadKey} />
          <LoadSizeInput
            shipmentKey={shipmentKey}
            loadKey={loadKey}
            field={loadConstants.FIELDS.ITEM_LENGTH}
            canBePredefined
          />
          <LoadSizeInput
            shipmentKey={shipmentKey}
            loadKey={loadKey}
            field={loadConstants.FIELDS.ITEM_WIDTH}
            canBePredefined
          />
          <LoadSizeInput
            shipmentKey={shipmentKey}
            loadKey={loadKey}
            field={loadConstants.FIELDS.ITEM_HEIGHT}
            canBePredefined={!isPallet}
            growLeft
          />
          <LoadSizeInput
            shipmentKey={shipmentKey}
            loadKey={loadKey}
            field={loadConstants.FIELDS.TOTAL_WEIGHT}
            customMax={maximumTruckWeight}
            growLeft
          />
        </div>
      </div>
    </div>
  );
};

LoadInputs.propTypes = {
  className: PropTypes.string.isRequired,
  shipmentKey: PropTypes.string.isRequired,
  loadKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isLast: PropTypes.bool,
  hasMultipleLoads: PropTypes.bool,
};

LoadInputs.defaultProps = {
  isLast: false,
  hasMultipleLoads: false,
};

const regularCSS = css`
  background-color: var(--grey100);
  border: 1px solid var(--grey300);
  border-radius: ${gridSizes('S')};
  margin-bottom: ${gridSizes('M')};

  .grid {
    width: 100%;
    display: grid;
    grid-gap: ${gridSizes('S')};
  }

  .title {
    padding-top: ${gridSizes('S')};
    line-height: normal;
    width: calc(100% - ${gridSizes('S')} - ${gridSizes('S')});
    justify-self: center;
  }

  .deleteContainer {
    text-align: right;
    padding-right: 8px;
    padding-top: 8px;
  }

  .inputs {
    padding: 16px;
    padding-top: 0;
  }

  /* non palletized */
  .grid {
    grid-template-columns: repeat(21, 1fr);
  }

  .header {
    grid-column: span 21;

    .title {
      grid-column: span 11;
    }

    .deleteContainer {
      grid-column: span 10;
    }
  }

  .inputs {
    grid-column: span 21;
    > div {
      grid-column: span 4;
    }
    > div:nth-child(1) {
      grid-column: span 9;
    }
    > div:nth-child(2) {
      grid-column: span 12;
    }
    > div:nth-child(3) {
      grid-column: span 5;
    }
  }

  .error {
    border: solid 1px red;
  }
  .not-error {
    border: solid 1px transparent;
  }
  .predefined {
    background-color: var(--grey200);
  }
`;

export default styled(LoadInputs)`
  ${regularCSS}
`;
